import { Compass, ArrowRight, Bug, ChevronRight, Flame } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "@/context/SubscriptionContext";
import { useAuth } from "@/App";
import { hasAccess, MEMBERSHIP_URL } from "@/lib/subscriptionAccess";
import PathFinderAvatar from "./PathFinderAvatar";

const TRUENORTH_STORE_URL = "https://www.truenorth-zone.com/collections";
const COSMIC_SUNFLOWER_IMAGE = "https://customer-assets.emergentagent.com/job_path-unfolds/artifacts/va4i7lvj_ChatGPT%20Image%20Feb%2014%2C%202026%2C%2012_49_01%20PM.png";

export default function ShopFooter() {
  const navigate = useNavigate();
  const { subscription } = useSubscription();
  const { user } = useAuth();
  const canAccessPathfinder = hasAccess(subscription.type, 'monthly', user);

  const handlePathfinderClick = () => {
    if (canAccessPathfinder) {
      navigate("/pathfinder");
    } else {
      window.open(MEMBERSHIP_URL, "_blank");
    }
  };

  return (
    <footer className="mt-12 pb-24 px-4">
      {/* Recommended Next Header */}
      <div className="flex items-center gap-2 mb-4">
        <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
          <Compass className="w-3 h-3" style={{ color: '#7b9e7b' }} />
        </div>
        <h3 className="text-sm font-bold" style={{ color: '#a69373' }}>Recommended Next</h3>
      </div>

      {/* PathFinder AI Coach Bar */}
      <div 
        onClick={handlePathfinderClick}
        className="mb-4 p-4 rounded-xl bg-gradient-to-r from-[#7b9e7b]/20 to-[#a69373]/20 border border-[#7b9e7b]/30 cursor-pointer hover:bg-[#7b9e7b]/30 transition-all"
        data-testid="footer-pathfinder-bar"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#7b9e7b] flex items-center justify-center">
            <PathFinderAvatar size={24} />
          </div>
          <div className="flex-1">
            <h4 className="text-white font-bold text-sm">PathFinder</h4>
            <p className="text-white/60 text-xs">AI Companion</p>
          </div>
          <ChevronRight className="w-5 h-5 text-white/40" />
        </div>
      </div>
      
      {/* Shop Banner */}
      <a 
        href={TRUENORTH_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="relative rounded-2xl overflow-hidden cursor-pointer group block"
        data-testid="shop-footer-banner"
      >
        {/* Background Image */}
        <div 
          className="w-full h-20 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
          style={{
            backgroundImage: `url('${COSMIC_SUNFLOWER_IMAGE}')`,
            backgroundPosition: 'center'
          }}
        >
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
          
          {/* Content */}
          <div className="absolute inset-0 flex items-center justify-between p-4">
            <div>
              <h4 className="text-base font-heading mb-0.5" style={{ color: '#ffffff', fontWeight: 700 }}>
                TrueNorth Store
              </h4>
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.8)', fontWeight: 700 }}>
                Journals, books & resources
              </p>
            </div>
            
            {/* Arrow Button */}
            <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center transition-all duration-300 group-hover:bg-white/30 group-hover:scale-110">
              <ArrowRight className="w-4 h-4" style={{ color: '#ffffff' }} />
            </div>
          </div>
        </div>
      </a>
      
      {/* Report a Bug Link */}
      <div className="mt-6 text-center space-y-2">
        <div className="flex justify-center gap-4">
          <span 
            className="text-xs text-white/40 hover:text-white/70 transition-colors cursor-pointer"
            onClick={() => navigate("/privacy")}
          >
            Privacy Notice
          </span>
          <span 
            className="text-xs text-white/40 hover:text-white/70 transition-colors cursor-pointer"
            onClick={() => navigate("/terms")}
          >
            Terms
          </span>
        </div>
        <a 
          href="mailto:info@truenorth-zone.com?subject=Bug%20Report%20%E2%80%93%20Compass%20App&body=Please%20describe%20the%20bug%20you%20encountered%3A%0A%0ADevice%3A%20%0AScreen%3A%20%0AWhat%20happened%3A%20"
          className="inline-flex items-center gap-2 text-xs text-white/40 hover:text-white/70 transition-colors"
          data-testid="footer-report-bug"
        >
          <Bug className="w-3 h-3" />
          Report a Bug
        </a>
      </div>
    </footer>
  );
}
